<template>
  <CModal
    :auto-focus="true"
    class="c-section-item-modal"
    show
    tabindex="-1"
    :to="attachTo"
    :trap-focus="true"
    :z-index="zIndex"
    @keydown.esc="cancel"
  >
    <div
      class="flex relative flex-col pa-4 gap-4 rounded-t-4 bg-white self-end ma-0 w-full h-[calc(100vh-40px)] slide-in-animation"
    >
      <main
        ref="rootRef"
        class="flex flex-auto flex-col overflow-auto items-center border-solid border-1 border-primary5 rounded-2"
        :class="{ 'justify-center': isScaleEnabled }"
      >
        <div
          class="w-full"
          :class="{ 'flex flex-col flex-auto overflow-hidden': isScaleEnabled }"
          :style="{ maxWidth: getMaxWidth(pitcherSettings, !!launchDarkly.enable_scale_content) }"
        >
          <slot :index="index" :section="section" />
        </div>
      </main>

      <footer class="flex justify-between gap-4 w-full">
        <CButton :disabled="isLoading" @click="cancel">
          {{ t('canvasUI.common.cancel') }}
        </CButton>

        <CButton :disabled="isLoading" :loading="isLoading" type="primary" @click="saveOrClose">
          {{ t('canvasUI.common.done') }}
        </CButton>
      </footer>
    </div>
  </CModal>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import CModal from '@lib/components/CModal/CModal.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import { computed, type ComputedRef, inject, provide, ref } from 'vue'
import CButton from '@lib/components/CButton/CButton.vue'
import { cloneDeep, isEqual } from 'lodash-es'
import { type SectionComponentOverrides } from '@canvas-builder/types/canvas'
import useCanvasSectionOverrides from '@canvas-builder/composables/useCanvasSectionOverrides.ts'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'
import useCanvas from '@canvas-builder/composables/useCanvas.ts'
import { type CanvasRetrieve } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

export type Emit = {
  close: [save?: boolean]
}

const props = defineProps<{
  zIndex?: number
  attachTo?: string | HTMLElement
  section: CanvasRetrieve['sections'][number]
  index: number
  saveCanvas: () => Promise<void>
}>()

const getSectionOverrides = () => sectionComponentOverrides.value?.[props.section.id]
const emit = defineEmits<Emit>()
const confirmation = useConfirmation()
const { t } = useI18n()
const { setSectionOverrides, sectionComponentOverrides } = useCanvasSectionOverrides()
const contentOverridesSnapshot = ref<SectionComponentOverrides | undefined>(cloneDeep(getSectionOverrides()))
const isContentOverridesChanged = () => !isEqual(getSectionOverrides(), contentOverridesSnapshot.value)
const isLoading = ref(false)
const rootRef = ref<HTMLElement | undefined>(undefined)

const isScaleEnabled = inject<ComputedRef<boolean>>(
  'isScaleEnabled',
  computed(() => false),
)

const revertContentOverrides = () => {
  setSectionOverrides(props.section.id, contentOverridesSnapshot.value)
}
const { getMaxWidth } = useCanvas()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const close = () => emit('close')

const saveOrClose = async () => {
  if (!isContentOverridesChanged()) {
    return emit('close', true)
  }

  try {
    isLoading.value = true
    await props.saveCanvas()
  } finally {
    isLoading.value = false
  }

  emit('close', true)
}

const cancel = () => {
  if (isContentOverridesChanged()) {
    confirmation.warning({
      title: t('canvasUI.canvasBuilder.confirmationOnUnsavedChanges.title'),
      content: t('canvasUI.canvasBuilder.confirmationOnUnsavedChanges.content'),
      confirmText: t('canvasUI.canvasBuilder.confirmationOnUnsavedChanges.confirm'),
      cancelText: t('canvasUI.canvasBuilder.confirmationOnUnsavedChanges.cancel'),
      exitText: t('canvasUI.canvasBuilder.confirmationOnUnsavedChanges.exit'),
      onConfirm: () => saveOrClose(),
      onExit: () => {
        revertContentOverrides()
        close()
      },
    })
  } else {
    close()
  }
}
provide(
  'attachTo',
  computed(() => rootRef.value),
)
</script>

<style lang="scss" scoped>
@keyframes slideInFromBottom {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.slide-in-animation {
  animation: slideInFromBottom 0.2s ease-out forwards;
}
</style>
